import { Button, Box, Tooltip } from "@mui/material"
import { navigateSubmitReview } from "authoring/routes/project/utilities/project-navigation"
import { confirm } from "lib/@dialogs/confirm"
import { submitDraftForReview } from "../../controller/submit-draft-for-review"
import { getMissingRequiredDraftAmends } from "../../controller/get-missing-required-draft-amends"
import { getUnappliedGroupsForSchedule } from "../../controller/get-unapplied-groups-for-schedule"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { getAmendsKeyLabel } from "authoring/routes/project/amends/lib/required-amends-helpers"
import { MissingAmendsDialog } from "./missing-amends-dialog"
import { MissingScheduleTypesDialog } from "./missing-schedule-types-dialog"
import { MissingScheduleGroupsDialog } from "./missing-schedule-groups-dialog"
import { UnappliedGroupsWarningDialog } from "./unapplied-groups-warning-dialog"
import { useDialog } from "lib/@hooks/useDialog"
import { Secure } from "lib/authorization/secure"
import { Frag } from "lib/@components/slot/frag"
import { AUTHORING_EDIT_SCHEDULES_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useScheduleEditorSeed } from "../../hooks/use-schedule-editor-seed"
import { SeedSource } from "authoring/lib/enum/seed-source.mjs"
import { useHasSavedEdits } from "../../hooks/use-has-saved-edits"
import { useScheduleEditorSaveDirty } from "../../hooks/use-schedule-editor-save-dirty"
import { useScheduleCodeUnavailable } from "../../hooks/use-schedule-code-unavailable"
import { getScheduleDraft } from "../../controller/get-schedule-draft"
import { DraftStatus } from "authoring/lib/enum/draft-status.mjs"
import { useScheduleEditorIds } from "../../hooks/use-schedule-editor-ids"
import { CollectUnsavedMissingAmends } from "../../schedule-editor-event-definitions.mjs"

const REJECTED_RESUBMIT_MESSAGE =
    "No new changes have been saved since this draft was rejected. Make and save a change before resubmitting."

export function SubmitForReview({ flushSave }) {
    const { projectId, scheduleId, draftId } = useScheduleEditorIds()
    const { rawSchedule: draft } = useBoundContext()
    const { seedSource } = useScheduleEditorSeed()
    const dirty = useScheduleEditorSaveDirty()
    const hasSavedEdits = useHasSavedEdits()
    const codeUnavailable = useScheduleCodeUnavailable()

    const isNewSchedule = seedSource === SeedSource.NEW
    const compareDisabled = dirty || !hasSavedEdits
    const disabled = (isNewSchedule ? dirty : compareDisabled) || codeUnavailable
    const tooltip = resolveTooltip()

    const confirmTitle = "Submit"
    const confirmMessage = "Submit for review? You won't be able to edit until review is complete."
    const missingAmendsDialog = useDialog(<MissingAmendsDialog />)
    const missingScheduleTypesDialog = useDialog(<MissingScheduleTypesDialog />)
    const missingScheduleGroupsDialog = useDialog(<MissingScheduleGroupsDialog />)
    const unappliedGroupsDialog = useDialog(<UnappliedGroupsWarningDialog />, { maxWidth: "sm", fullWidth: true })

    return (
        <Secure demand={AUTHORING_EDIT_SCHEDULES_DEMAND} fallback={<Frag />}>
            <Tooltip title={disabled && tooltip ? tooltip : ""} enterDelay={0} leaveDelay={0}>
                <Box component="span" sx={{ display: "inline-block" }}>
                    <Button
                        onClick={() =>
                            handleSubmitReview({
                                projectId,
                                scheduleId,
                                draftId,
                                isNewSchedule,
                                confirmTitle,
                                confirmMessage,
                                draft,
                                missingAmendsDialog,
                                missingScheduleTypesDialog,
                                missingScheduleGroupsDialog,
                                unappliedGroupsDialog,
                                flushSave,
                                dirty,
                            })
                        }
                        variant="contained"
                        disabled={disabled}
                        sx={disabled ? { pointerEvents: "none" } : undefined}
                        data-cy="submit-for-review-button"
                    >
                        Submit For Review
                    </Button>
                </Box>
            </Tooltip>
        </Secure>
    )

    function resolveTooltip() {
        if (codeUnavailable) return "Schedule code must be unique before submitting for review."
        if (!isNewSchedule && !hasSavedEdits) return "There are no changes to submit for review."
        if (dirty) return "Save changes before submitting."
        return ""
    }
}

async function handleSubmitReview({
    projectId,
    scheduleId,
    draftId,
    isNewSchedule,
    confirmTitle,
    confirmMessage,
    draft,
    missingAmendsDialog,
    missingScheduleTypesDialog,
    missingScheduleGroupsDialog,
    unappliedGroupsDialog,
    flushSave,
    dirty,
}) {
    try {
        // Flush before anything reads the draft status. Saving content is what moves a
        // rejected draft back to editing, so an unflushed edit would make the status read
        // below report REJECTED for a draft the author has in fact just changed.
        if (dirty && flushSave) await flushSave()

        const savedMissingAmendsKeys = (await getMissingRequiredDraftAmends({ projectId, scheduleId })) ?? []
        const missingRequiredAmendsKeys = isNewSchedule
            ? savedMissingAmendsKeys
            : addUnsavedMissingAmends(savedMissingAmendsKeys, draftId)
        if (missingRequiredAmendsKeys.length > 0) {
            await showMissingAmendsDialog({
                missingRequiredAmendsKeys,
                draft,
                missingAmendsDialog,
                projectId,
                scheduleId,
            })
            return
        }

        const unappliedGroups = (await getUnappliedGroupsForSchedule({ projectId, scheduleId })) ?? []
        if (unappliedGroups.length > 0) {
            const proceed = await unappliedGroupsDialog({ groups: unappliedGroups, projectId })
            if (!proceed) return
        } else {
            const confirmReview = await confirm(confirmMessage, confirmTitle)
            if (!confirmReview) return
        }

        // This used to also require `!dirty`, which is only ever true for unsaved *content*
        // edits. An author-note edit is a real change but never sets dirty — the server
        // moves a rejected draft back to editing when a note is saved. Because every
        // pending save has been flushed above, a fresh read that still says REJECTED is on
        // its own the honest signal that nothing has changed since the rejection.
        const draftAfterConfirm = await getScheduleDraft({ projectId, scheduleId })
        if (draftAfterConfirm?.status === DraftStatus.REJECTED) {
            errorSnackbar(REJECTED_RESUBMIT_MESSAGE)
            return
        }

        const result = await submitDraftForReview({ projectId, scheduleId })
        const missingRequiredAmendsOnSubmit = result?.missingRequiredAmendsKeys ?? []

        if (missingRequiredAmendsOnSubmit.length > 0) {
            await showMissingAmendsDialog({
                missingRequiredAmendsKeys: missingRequiredAmendsOnSubmit,
                draft,
                missingAmendsDialog,
                projectId,
                scheduleId,
            })
        } else if (result?.success) {
            successSnackbar("Draft successfully submitted for review")
            navigateSubmitReview({ projectId, scheduleId })
        } else {
            errorSnackbar("Draft could not be submitted for review")
        }
    } catch (error) {
        console.error("Failed to submit draft for review", error)
        if (error?.message === "At least one schedule type must be selected before submitting for review.") {
            await missingScheduleTypesDialog()
            return
        }
        if (error?.message === "At least one schedule group must be selected before submitting for review.") {
            await missingScheduleGroupsDialog()
            return
        }
        errorSnackbar(getSubmitReviewErrorMessage(error) || "Draft could not be submitted for review")
    }
}

// A required note the author has emptied on screen cannot be saved empty, so the server still
// counts the old one. Ask the open note editors so it shows as missing too.
function addUnsavedMissingAmends(missingRequiredAmendsKeys, draftId) {
    const unsaved = { amendsKeys: [] }
    CollectUnsavedMissingAmends(draftId).call(unsaved)
    return [...new Set([...missingRequiredAmendsKeys, ...unsaved.amendsKeys])]
}

function getSubmitReviewErrorMessage(error) {
    return error?.graphQLErrors?.[0]?.message ?? error?.message ?? null
}

function buildMissingAmendsItems({ missingRequiredAmendsKeys, draft }) {
    return missingRequiredAmendsKeys.map((amendsKey) => ({
        amendsKey,
        label: getAmendsKeyLabel({ amendsKey, draft }),
    }))
}

function showMissingAmendsDialog({ missingRequiredAmendsKeys, draft, missingAmendsDialog, projectId, scheduleId }) {
    return missingAmendsDialog({
        items: buildMissingAmendsItems({ missingRequiredAmendsKeys, draft }),
        projectId,
        scheduleId,
    })
}
